import { useEffect, useState, useCallback, useRef } from "react";
import { IResourceData } from "../types/resource";
import {
  getResourceColumnsPaginatedList,
  getResourceColumnsPaginatedListByDomainId,
} from "../services/resourcesService";
import { IPaginatedProps } from "../types";

interface IUseFetchResourceColumns extends IPaginatedProps {
  searchTerm?: string;
  resourceColumnFilterIds?: string; // NEW: Comma-separated resource column filter IDs
  isActive?: string; // "true" | "false" | "all"
}

const useFetchPaginatedResourceColumnsByDomain = ({
  currentDomainId,
  setIsLoading,
  page,
  pSize,
  searchTerm,
  resourceColumnFilterIds, // NEW: Resource column filter IDs parameter
  isActive = "true",
}: IUseFetchResourceColumns) => {
  const [resourcesData, setResourcesData] = useState<IResourceData[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(
    async (searchQuery?: string) => {
      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      setIsLoading(true);
      try {
        const result: IResourceData[] =
          await getResourceColumnsPaginatedListByDomainId({
            currentDomainId,
            page,
            pSize,
            searchTerm: searchQuery || searchTerm,
            resourceColumnFilterIds, // NEW: Pass resource column filter IDs
            isActive,
            signal: abortControllerRef.current.signal,
          });
        setResourcesData(result);
      } catch (err: any) {
        // Don't update state if request was aborted
        if (err.name !== "AbortError") {
          setResourcesData([]);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      currentDomainId,
      page,
      pSize,
      searchTerm,
      resourceColumnFilterIds,
      isActive,
      setIsLoading,
    ],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Cleanup function to abort any pending requests
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return [resourcesData, fetchData];
};

export default useFetchPaginatedResourceColumnsByDomain;
